import { ClassList } from 'lib/utils/react/classlist';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

export type PopupKey = string;

export interface PopupElementProps {
  isClosing: boolean;
  setClosing: () => void;
}

export interface PopupItemOpts {
  component: React.FC<PopupElementProps>;
  width?: 'default' | 'wide';
  allowOutsideClick?: boolean;
  key?: PopupKey;
}

export interface PopupProviderOpts {
  enqueuePopup: (opts: PopupItemOpts) => PopupKey;
}

export const PopupProviderContext = React.createContext<PopupProviderOpts>(
  {} as PopupProviderOpts
);

export const PopupProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<{
    current: PopupItemOpts | undefined;
    queue: PopupItemOpts[];
  }>({
    queue: [],
    current: undefined
  });

  const enqueuePopup = ({
    component,
    allowOutsideClick,
    key,
    width
  }: PopupItemOpts): PopupKey => {
    const opts = {
      component,
      allowOutsideClick: allowOutsideClick ?? true,
      key,
      width
    };

    opts.key = popupKey(opts.key);

    setState((prev) => {
      if (prev.current) {
        return {
          current: prev.current,
          queue: [...prev.queue, opts]
        };
      } else {
        return {
          current: opts,
          queue: prev.queue
        };
      }
    });

    return opts.key;
  };

  return (
    <PopupProviderContext.Provider value={{ enqueuePopup }}>
      {children}
      {state.current ? (
        <PopupRenderer
          key={state.current.key}
          onRemoved={() => {
            setState((prev) => {
              if (prev.queue.length > 0) {
                return {
                  current: prev.queue[0],
                  queue: prev.queue.slice(1)
                };
              } else {
                return { current: undefined, queue: [] };
              }
            });
          }}
          current={state.current}
        />
      ) : null}
    </PopupProviderContext.Provider>
  );
};

const OPEN_CLOSE_DURATION = 350;

const PopupRenderer: React.FC<{
  current: PopupItemOpts;
  onRemoved: () => void;
}> = ({ current, onRemoved }) => {
  const Element = current.component;
  const [isClosing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Wait a few scrolling on body
  useEffect(() => {
    if (isClosing) {
      setTimeout(onRemoved, OPEN_CLOSE_DURATION);
    }
  }, [isClosing]);

  useEffect(() => {
    // Disable scrolling while popup is active
    document.body.classList.add('no-scroll');
    // Enable back on when popup is removed
    return () => document.body.classList.remove('no-scroll');
  });

  useEffect(() => {
    // Close popup if user clicks outside the popup
    const pageClickEvent = (e: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(e.target as HTMLElement)) {
        setClosing(true);
      }
    };
    if (!!current.allowOutsideClick && !isClosing) {
      setTimeout(() => {
        window.addEventListener('click', pageClickEvent);
      }, OPEN_CLOSE_DURATION);
    }
    return () => window.removeEventListener('click', pageClickEvent);
  }, [isClosing, !!current.allowOutsideClick]);

  const backdropClasses = ClassList.of('popup-backdrop')
    .add('is-closing', isClosing)
    .toString();

  const cardClasses = ClassList.of('popup-card')
    .add('is-wide', current.width === 'wide')
    .add('is-closing', isClosing)
    .toString();

  return (
    <div className="popup-component" key={current.key}>
      <div className={backdropClasses}></div>
      <div className="popup-wrapper">
        <div className={cardClasses} ref={!!current.allowOutsideClick ? ref : null}>
          <Element isClosing={isClosing} setClosing={() => setClosing(true)} />
        </div>
      </div>
    </div>
  );
};

const popupKey = (providedKey?: PopupKey): PopupKey => {
  if (providedKey) {
    return providedKey;
  } else {
    return 'popup:key:' + new Date().getMilliseconds() + ':' + Math.random();
  }
};
